import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn, Skeleton } from "@nexus/ui";
import type { BigQueryUsageMonitoring } from "@/features/bigquery-usage/api/bigquery-usage.api";
import { formatBytesCompact, formatBytesExact } from "../lib/bytes";
import {
  RECONCILED_TOLERANCE,
  summarizeBigQueryReconciliation,
  type BigQueryMetricReconciliation,
} from "../lib/reconciliation";

export interface BigQueryReconciliationPanelProps {
  /** Undefined until the Cloud Monitoring read resolves. */
  monitoring: BigQueryUsageMonitoring | undefined;
  isLoading: boolean;
  /** What Nexus recorded for the same window. */
  recordedQueries: number;
  recordedBytesBilled: number;
  /**
   * Whether a narrowing is active. Cloud Monitoring accounts for the whole GCP
   * project and takes no filter, so a narrowed recorded total has nothing
   * comparable on the other side.
   */
  isNarrowed: boolean;
}

const NOTE_CLASS = "text-[11px] text-muted-foreground";
const LABEL_CLASS = "text-[11px] uppercase tracking-wider text-muted-foreground";
const FIGURE_CLASS = "text-xs tabular-nums text-foreground";
const FRACTION_TO_PERCENT = 100;
const DASH = "—";

/**
 * What Nexus recorded, next to what Google itself accounted for.
 *
 * One job, and it is about honesty. The comparison says how much of Google's own
 * figure the tab above explains, and names the four structural reasons the two
 * will never match exactly (R21) rather than blaming the whole gap on usage
 * outside Nexus — three of the four are Nexus's own recording, so that
 * attribution would be false here.
 *
 * R19's zero states are named by `BigQueryZeroStateNotice` at the top of the
 * tab, not here: they explain the figures above this panel, so they have to be
 * read before them. Monitoring being unreadable is this panel's own degrade
 * state under R20 and nothing more — it renders as unavailable here and leaves
 * the zero state, and every figure on the tab, exactly as they were.
 *
 * Rendered as the body of `BigQueryReconciliationDialog`, which is why it
 * carries no surface of its own and does not repeat the verdict its trigger
 * already states.
 */
export function BigQueryReconciliationPanel({
  monitoring,
  isLoading,
  recordedQueries,
  recordedBytesBilled,
  isNarrowed,
}: BigQueryReconciliationPanelProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const summary = useMemo(
    () =>
      summarizeBigQueryReconciliation(monitoring, {
        queries: recordedQueries,
        bytesBilled: recordedBytesBilled,
      }),
    [monitoring, recordedQueries, recordedBytesBilled],
  );

  const tolerancePercent = `${RECONCILED_TOLERANCE * FRACTION_TO_PERCENT}%`;

  return (
    <div className="flex min-h-0 flex-col gap-3">
      {isLoading && !monitoring ? (
        <Skeleton className="h-16 w-full rounded-sm bg-foreground/8" />
      ) : isNarrowed ? (
        // Comparing a narrowed total against a project-wide one would report a
        // discrepancy the filter itself created.
        <p className={NOTE_CLASS}>
          {t(
            "admin.bigQueryUsage.reconciliation.narrowed",
            "Cloud Monitoring accounts for the whole GCP project and takes no filter, so there is nothing to compare a narrowed total against. Clear the filters to reconcile the window.",
          )}
        </p>
      ) : summary.status === "unavailable" ? (
        <p className={NOTE_CLASS}>
          {t(
            "admin.bigQueryUsage.reconciliation.unavailable",
            "Cloud Monitoring could not be read from this environment, so there is nothing to reconcile against. Everything else on this tab is what Nexus recorded.",
          )}
        </p>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <MetricComparison
              label={t("admin.bigQueryUsage.table.queries", "Queries")}
              metric={summary.queries}
              format={(value) => value.toLocaleString(locale)}
            />
            <MetricComparison
              label={t("admin.bigQueryUsage.table.bytesBilled", "Bytes billed")}
              metric={summary.bytesBilled}
              format={(value) => formatBytesCompact(value, locale)}
              exact={(value) => formatBytesExact(value, locale)}
            />
          </div>

          <p className={NOTE_CLASS}>
            {summary.status === "agreement"
              ? t(
                  "admin.bigQueryUsage.reconciliation.agreementNote",
                  "The gap sits inside the {{percent}} band the two sides always differ by, so what Nexus recorded accounts for what Google billed this project for.",
                  { percent: tolerancePercent },
                )
              : t(
                  "admin.bigQueryUsage.reconciliation.discrepancyNote",
                  "The gap is wider than the {{percent}} band the two sides normally differ by.",
                  { percent: tolerancePercent },
                )}
          </p>

          <GapCauses />
        </>
      )}
    </div>
  );
}

interface MetricComparisonProps {
  label: string;
  metric: BigQueryMetricReconciliation;
  format: (value: number) => string;
  /** The full-precision figure for a hover, where one exists. */
  exact?: (value: number) => string;
}

/** One measure from both sides, with the gap already taken. */
function MetricComparison({ label, metric, format, exact }: MetricComparisonProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const share =
    metric.gapShare == null
      ? null
      : `${(metric.gapShare * FRACTION_TO_PERCENT).toLocaleString(locale, {
          maximumFractionDigits: 1,
        })}%`;

  return (
    <div className="flex flex-col gap-1 rounded-sm border border-border px-3 py-2">
      <span className={LABEL_CLASS}>{label}</span>
      <dl className="flex flex-col gap-0.5">
        <div className="flex items-baseline justify-between gap-3">
          <dt className={NOTE_CLASS}>
            {t("admin.bigQueryUsage.reconciliation.recorded", "Nexus recorded")}
          </dt>
          <dd className={FIGURE_CLASS} title={exact?.(metric.recorded)}>
            {format(metric.recorded)}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className={NOTE_CLASS}>
            {t("admin.bigQueryUsage.reconciliation.reported", "Google reported")}
          </dt>
          <dd
            className={cn(FIGURE_CLASS, !metric.isAvailable && "text-muted-foreground")}
            title={metric.isAvailable ? exact?.(metric.reported) : undefined}
          >
            {/* A metric Monitoring never returned is not a zero it measured. */}
            {metric.isAvailable
              ? format(metric.reported)
              : t("admin.bigQueryUsage.reconciliation.metricUnavailable", "not reported")}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-3">
          <dt className={NOTE_CLASS}>{t("admin.bigQueryUsage.reconciliation.gap", "Gap")}</dt>
          <dd className="flex items-baseline gap-1.5">
            {metric.isAvailable ? (
              <>
                <span className={FIGURE_CLASS}>{format(metric.gap)}</span>
                {share ? <span className={NOTE_CLASS}>{share}</span> : null}
              </>
            ) : (
              <span className={cn(FIGURE_CLASS, "text-muted-foreground")}>{DASH}</span>
            )}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * R21's four structural causes.
 *
 * Three of them are Nexus's own recording rather than traffic it never saw,
 * which is why the copy cannot say what the Vertex panel's does: attributing the
 * whole gap to usage outside Nexus would send an operator hunting for a rogue
 * script when the answer is a retry, a failure, or a pod that stopped.
 */
function GapCauses() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-1">
      <span className={LABEL_CLASS}>
        {t("admin.bigQueryUsage.reconciliation.causesTitle", "Why the two never match exactly")}
      </span>
      <ul className={cn(NOTE_CLASS, "flex flex-col gap-0.5")}>
        <li>
          {t(
            "admin.bigQueryUsage.reconciliation.causeFailedQueries",
            "Failed queries: Google bills the bytes a query scanned before it errored, and Nexus records the failure with no billed total of its own.",
          )}
        </li>
        <li>
          {t(
            "admin.bigQueryUsage.reconciliation.causeRetriedAttempts",
            "Retried attempts: Google bills every attempt of a retried query, while Nexus records the execution once.",
          )}
        </li>
        <li>
          {t(
            "admin.bigQueryUsage.reconciliation.causeShutdownLoss",
            "Shutdown loss: a pod that stops between running a query and writing its row takes that row with it.",
          )}
        </li>
        <li>
          {t(
            "admin.bigQueryUsage.reconciliation.causeOutsideNexus",
            "Usage outside Nexus: the BigQuery console, a scheduled query, or a script run against the same GCP project is billed here and never recorded by Nexus.",
          )}
        </li>
      </ul>
    </div>
  );
}
